import { skipToken, useSuspenseQuery } from '@tanstack/react-query';
import type * as React from 'react';

import type { NavigationScreenRoot } from '@gitbook/app-navigation';
import { localStorage } from '@gitbook/util-local-storage';

import {
    getFirstSpaceInOrgAllContent,
    getSiteFromOrgAllContent,
    getSpaceFromOrgAllContent,
} from '../../Components';
import {
    LAST_SEEN_CONTENT_KEY,
    NavigationRedirect,
    parseNavigationState,
    useNavigationState,
} from '../../navigation';
import { apiQuery, parseOnboardingParamsFromURL, shouldRedirectToWelcomeScreen } from '../../state';

/**
 * Handle the root route ("/" or "/o/:org")
 */
export function RootRedirect(props: { screen: NavigationScreenRoot }): React.ReactElement {
    const { screen } = props;
    const lastContent = parseNavigationState(localStorage.get<unknown>(LAST_SEEN_CONTENT_KEY));
    const navigationState = useNavigationState();

    const {
        data: { items: allUserOrganizations },
    } = useSuspenseQuery(apiQuery('listOrganizationsForAuthenticatedUser', { limit: 100 }));
    const userOrganizations = allUserOrganizations.map((org) => org.id);

    // Find the relevant organization to redirect to.
    // - If an organization is specified in the URL, we use that.
    // - If the last seen content is a space, we use the organization of that space (if the user has access).
    // - Else we use the first organization the user has access to.
    const organizationId = (() => {
        if (screen.organization && userOrganizations.includes(screen.organization)) {
            return screen.organization;
        }

        const lastContentOrg =
            lastContent && 'organization' in lastContent.screen && lastContent.screen.organization
                ? lastContent.screen.organization
                : null;

        if (lastContentOrg && userOrganizations.includes(lastContentOrg)) {
            return lastContentOrg;
        }

        return userOrganizations[0] ?? null;
    })();

    const { data: allContent } = useSuspenseQuery(
        apiQuery('getOrganizationAllContent', organizationId ? { organizationId } : skipToken)
    );

    // Redirect to the welcome screen if we have Onboarding params
    if (shouldRedirectToWelcomeScreen(parseOnboardingParamsFromURL())) {
        return (
            <NavigationRedirect
                to={{
                    current: {
                        type: 'welcome',
                    },
                }}
                replace
            />
        );
    }

    // Redirect to last content if the user can still access it (and not deleted)
    // If opening an org, we only redirect to last content in the org itself
    if (lastContent?.screen) {
        const lastContentScreen = lastContent.screen;

        const isValidRedirect = (() => {
            // Unusual but if the API doesn't return we exit.
            if (!allContent) {
                return false;
            }

            if (lastContentScreen.type === 'space') {
                return getSpaceFromOrgAllContent(allContent, lastContentScreen.space);
            }

            if (lastContentScreen.type === 'site') {
                return getSiteFromOrgAllContent(allContent, lastContentScreen.site);
            }

            if (lastContentScreen.type === 'home') {
                return lastContentScreen.organization === organizationId;
            }

            return false;
        })();

        if (isValidRedirect) {
            return (
                <NavigationRedirect
                    to={{
                        screen: lastContentScreen,
                        overlay: navigationState.overlay,
                    }}
                />
            );
        }
    }

    // Redirect to first space if any.
    const firstSpace = allContent ? getFirstSpaceInOrgAllContent(allContent) : undefined;

    if (firstSpace) {
        return (
            <NavigationRedirect
                to={{
                    screen: {
                        type: 'space',
                        organization: firstSpace.space.organization,
                        space: firstSpace.space.id,
                        page: '',
                    },
                    overlay: navigationState.overlay,
                }}
            />
        );
    }

    // Redirect to home dashboard if org is empty
    if (organizationId) {
        return (
            <NavigationRedirect
                to={{
                    screen: {
                        type: 'home',
                        organization: organizationId,
                    },
                    overlay: navigationState.overlay,
                }}
            />
        );
    }

    return (
        <NavigationRedirect
            to={{
                screen: {
                    type: 'welcome',
                },
                overlay: null,
            }}
        />
    );
}
